import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher, Footer } from "@/components/common";
import { MessagesSquare, GitMerge, ScrollText, Landmark, ShieldCheck, Users, ArrowRight, Building2, FileSearch } from "lucide-react";

const how = [
  { icon: MessagesSquare, n: "01", title: "Create or join a debate", desc: "Debates live inside a political jurisdiction (city → region → country → EU) or a dedicated Deliberative Space, and are organised by policy area.", tone: "coral" },
  { icon: Users, n: "02", title: "Deliberate with positions & sources", desc: "Eligible citizens and experts declare a position, then argue, reply, amend and cite evidence in timed rounds — guided by a neutral AI mediator.", tone: "blue" },
  { icon: GitMerge, n: "03", title: "Reach two-stage consensus", desc: "AI drafts a compromise proposal needing a two-thirds participant majority, then a jurisdiction-wide public vote (50%+1).", tone: "navy" },
  { icon: ScrollText, n: "04", title: "Track action by representatives", desc: "Approved proposals are delivered to representatives, who endorse, submit to the legislature and upload evidence — all tracked publicly.", tone: "coral" },
];

const who = [
  { icon: Users, title: "Citizens", desc: "Join instantly, deliberate on the issues you care about, and vote on community proposals." },
  { icon: FileSearch, title: "Experts & Organisations", desc: "NGOs, research bodies and experts add verified expertise backed by uploaded evidence." },
  { icon: Landmark, title: "Public officials & representatives", desc: "Receive community proposals, act on them, and report legislative progress transparently." },
];

const TONE_BG = { coral: "bg-primary text-white", blue: "bg-secondary text-white", navy: "bg-foreground text-white" };

const NAVY = "#0A192F", CORAL = "#FF5252", BLUE = "#3B82F6", CREAM = "#FAF9F6";
const WORD_PUZZLE_URL = "https://static.prod-images.emergentagent.com/jobs/8d66e6ad-6fdf-44ec-b140-3387824d864f/images/c1e960518136710fc97c29d212a83a729c1c9eceb1b99dea2ffda8f9bae2290f.jpeg";
const FADE_MASK = "linear-gradient(to right, transparent 0%, black 14%, black 86%, transparent 100%)";

// Four interlocking jigsaw pieces assembling into one square — collaboration.
function CollabPuzzle({ className = "", style }) {
  const common = { stroke: NAVY, strokeWidth: 5, strokeLinejoin: "round" };
  return (
    <svg viewBox="-6 -6 212 212" className={className} style={{ filter: "drop-shadow(5px 5px 0 " + NAVY + ")", ...style }} aria-hidden="true">
      {/* top-left (coral) */}
      <path d="M4 4 H100 V37 A13 13 0 0 1 100 63 V100 H63 A13 13 0 0 1 37 100 H4 Z" fill={CORAL} {...common} />
      {/* top-right (blue) */}
      <path d="M100 4 H196 V100 H163 A13 13 0 0 1 137 100 H100 V63 A13 13 0 0 0 100 37 V4 Z" fill={BLUE} {...common} />
      {/* bottom-left (cream) */}
      <path d="M4 100 H37 A13 13 0 0 0 63 100 H100 V137 A13 13 0 0 1 100 163 V196 H4 Z" fill={CREAM} {...common} />
      {/* bottom-right (navy) */}
      <path d="M100 100 H137 A13 13 0 0 0 163 100 H196 V196 H100 V163 A13 13 0 0 0 100 137 V100 Z" fill={NAVY} {...common} />
    </svg>
  );
}

// A little suspension bridge — connection / coming together.
function Bridge({ className = "", style }) {
  return (
    <svg viewBox="0 0 240 120" className={className} style={style} fill="none" aria-hidden="true">
      <line x1="8" y1="96" x2="232" y2="96" stroke={NAVY} strokeWidth="5" strokeLinecap="round" />
      <line x1="66" y1="96" x2="66" y2="26" stroke={NAVY} strokeWidth="4" />
      <line x1="174" y1="96" x2="174" y2="26" stroke={NAVY} strokeWidth="4" />
      <path d="M8 92 L66 26 Q120 78 174 26 L232 92" stroke={CORAL} strokeWidth="4.5" strokeLinejoin="round" />
      {[24, 42, 90, 108, 132, 150, 198, 216].map((x, i) => {
        const t = x <= 66 ? (66 - x) / 58 : x >= 174 ? (x - 174) / 58 : 1 - Math.abs(120 - x) / 54;
        const cy = 26 + (1 - Math.max(0, Math.min(1, t))) * 62;
        return <line key={i} x1={x} y1={cy} x2={x} y2="96" stroke={BLUE} strokeWidth="2.5" />;
      })}
    </svg>
  );
}

function HeroDecor() {
  const S = (s) => ({ boxShadow: "4px 4px 0 0 " + NAVY, ...s });
  return (
    <div className="pointer-events-none absolute inset-0 hidden md:block" aria-hidden="true">
      {/* ★ collaboration centrepiece — a word-puzzle of civic ideas coming together */}
      <div className="floaty absolute top-[9%] right-[3%] w-64 lg:w-[22rem] rounded-2xl overflow-hidden border-2" style={{ animationDelay: "0.4s", borderColor: NAVY, boxShadow: "6px 6px 0 0 " + NAVY }}>
        <img src={WORD_PUZZLE_URL} alt="A puzzle of civic ideas fitting together" className="w-full h-auto block" loading="lazy" />
      </div>
      {/* small dot */}
      <div className="floaty absolute top-16 right-[30%] w-8 h-8 rounded-full border-2 bg-primary" style={S({ animationDelay: "1.4s" })} />
      {/* triangle */}
      <svg className="floaty absolute top-24 right-[40%] w-12 h-12" style={{ animationDelay: "0.6s" }} viewBox="0 0 60 60">
        <polygon points="30,4 56,54 4,54" fill={CORAL} stroke={NAVY} strokeWidth="4" strokeLinejoin="round" />
      </svg>
      {/* zigzag */}
      <svg className="floaty absolute top-[45%] right-[34%] w-24 h-8" style={{ animationDelay: "1s" }} viewBox="0 0 120 30" fill="none">
        <polyline points="4,24 24,6 44,24 64,6 84,24 104,6 116,24" stroke={CORAL} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {/* plus */}
      <svg className="floaty absolute top-[60%] right-[44%] w-9 h-9" style={{ animationDelay: "2.4s" }} viewBox="0 0 40 40">
        <path d="M15 4 h10 v11 h11 v10 h-11 v11 h-10 v-11 h-11 v-10 h11 z" fill={NAVY} stroke={NAVY} strokeWidth="2" strokeLinejoin="round" />
      </svg>
      {/* hexagon */}
      <svg className="floaty absolute bottom-[30%] right-[10%] w-14 h-14" style={{ animationDelay: "0.3s" }} viewBox="0 0 60 60">
        <polygon points="30,4 52,17 52,43 30,56 8,43 8,17" fill={CREAM} stroke={NAVY} strokeWidth="4" strokeLinejoin="round" />
      </svg>
      {/* diamond */}
      <div className="floaty absolute bottom-[14%] right-[36%] w-8 h-8 border-2 bg-secondary" style={S({ animationDelay: "2.2s", borderColor: NAVY, transform: "rotate(45deg)" })} />
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b-2 border-foreground bg-background sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-display font-black text-2xl tracking-tight text-foreground">Deliber<span className="text-primary">.</span>Act</span>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to={user ? "/dashboard" : "/login"} data-testid="landing-login" className="text-sm font-bold text-foreground hover:text-primary transition-colors px-3 py-2">{user ? t("Dashboard") : t("Log in")}</Link>
            <Link to={user ? "/dashboard" : "/register"} data-testid="landing-register" className="nb-btn bg-primary text-white text-sm px-4 py-2">{user ? t("Enter") : t("Get started")}</Link>
          </div>
        </div>
      </header>

      {/* Pencil word-puzzle banner (readable) on the circle-dotted background */}
      <div className="bg-civic-pattern border-b-2 border-foreground" data-testid="top-banner">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center gap-6 py-6">
          <div className="shrink-0 text-center sm:text-left">
            <p className="overline mb-1">{t("Civic deliberation platform")}</p>
            <p className="font-display text-lg sm:text-2xl font-black tracking-tight text-foreground leading-tight max-w-xs">{t("Where citizens connect, deliberate and decide — together.")}</p>
          </div>
          <img src={WORD_PUZZLE_URL} alt={t("A hand-drawn puzzle of civic words fitting together")} data-testid="banner-puzzle" loading="eager"
               className="flex-1 min-w-0 w-full h-32 sm:h-40 object-cover object-center mix-blend-multiply pointer-events-none select-none"
               style={{ maskImage: FADE_MASK, WebkitMaskImage: FADE_MASK }} />
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden bg-civic-pattern border-b-2 border-foreground">
        <div className="relative max-w-7xl mx-auto px-6 py-20 sm:py-28">
          <span className="nb-pill bg-white text-foreground text-xs px-4 py-1.5 mb-6" data-testid="hero-tag">{t("Civic deliberation, from debate to law")}</span>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95] text-foreground max-w-4xl">
            {t("Turn public debate into proposals representatives act on.")}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-foreground/80 max-w-2xl leading-relaxed font-medium">{t("Deliber.Act is a civic participation platform where citizens, experts, organisations and elected officials deliberate, reach consensus, and track real legislative action — within jurisdictions and dedicated deliberative spaces.")}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to={user ? "/dashboard" : "/register"} data-testid="hero-cta-primary" className="nb-btn bg-primary text-white text-base px-7 py-3.5">{user ? t("Go to dashboard") : t("Get started")} <ArrowRight size={18} /></Link>
            <Link to="/login" data-testid="hero-cta-login" className="nb-btn bg-white text-foreground text-base px-7 py-3.5">{t("I have an account")}</Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <p className="overline mb-2">{t("How it works")}</p>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mb-10">{t("From conversation to consensus to action.")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {how.map((f) => (
            <div key={f.n} className="nb-card nb-hover p-7" data-testid="how-card">
              <div className="flex items-center justify-between mb-5">
                <span className={`w-12 h-12 rounded-xl border-2 border-foreground flex items-center justify-center ${TONE_BG[f.tone]}`}><f.icon size={22} /></span>
                <span className="font-display font-black text-3xl text-foreground/15">{f.n}</span>
              </div>
              <h3 className="font-display text-lg font-extrabold tracking-tight mb-2">{t(f.title)}</h3>
              <p className="text-muted-foreground leading-relaxed text-sm font-medium">{t(f.desc)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Who it's for */}
      <section className="border-y-2 border-foreground bg-secondary/10 bg-civic-grid">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="overline mb-2">{t("Who it's for")}</p>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mb-10">{t("Everyone with a stake in public decisions.")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {who.map((f) => (
              <div key={f.title} className="nb-card nb-hover p-7" data-testid="who-card">
                <span className="w-12 h-12 rounded-xl border-2 border-foreground bg-secondary text-white flex items-center justify-center mb-4"><f.icon size={22} /></span>
                <h3 className="font-display text-lg font-extrabold tracking-tight mb-2">{t(f.title)}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm font-medium">{t(f.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliberative Spaces CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="nb-card bg-foreground text-white p-10 sm:p-14 rounded-3xl" data-testid="spaces-banner">
          <div className="flex items-start gap-4">
            <span className="shrink-0 w-14 h-14 rounded-2xl border-2 border-white bg-primary flex items-center justify-center"><Building2 size={26} /></span>
            <div>
              <p className="overline text-primary mb-2">{t("Deliberative Spaces")}</p>
              <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mb-3">{t("Host a focused programme or movement.")}</h2>
              <p className="text-white/85 leading-relaxed max-w-2xl mb-6 font-medium">{t("Beyond geographic jurisdictions, Deliber.Act hosts dedicated Deliberative Spaces for coalitions, assemblies, campaigns and initiatives — a focused home to build a shared programme and turn it into proposals. Create your own space and invite others to join.")}</p>
              <Link to={user ? "/dashboard" : "/register"} data-testid="cta-create-space" className="nb-btn bg-white text-foreground px-6 py-3">{t("Create your own space")} <ArrowRight size={16} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="border-t-2 border-foreground bg-background">
        <div className="max-w-7xl mx-auto px-6 py-14 flex items-center gap-4">
          <span className="shrink-0 w-12 h-12 rounded-xl border-2 border-foreground bg-secondary text-white flex items-center justify-center"><ShieldCheck size={24} /></span>
          <p className="text-foreground/80 font-medium"><span className="font-extrabold text-foreground">{t("Trust by design.")}</span> {t("Citizens join instantly; organisations, officials and representatives are verified by administrators, and expertise is backed by uploaded evidence.")}</p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
